import * as _ from 'underscore';
import { IServerSettingsScope } from '../../settings/controllers/ServerSettingsFormCtrl';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../settings/interfaces/ISite';
import { ISiteSettingsForSiteAdmin } from '../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { Module } from '../../ts/Module';
import { Sites } from '../../ts/resources/Sites';
import { IExtensionsServerSettings } from '../../ts/ServerApi/types/IEditableServerSettings';
import { IError } from '../../ts/ServerApi/types/IError';
import { ServerService } from '../../ts/services/ServerService';
import { ISiteAuthenticationSettings } from '../../ts/services/SiteAuthenticationSettingsService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

/**
* This directive allows us to coordinate server and site settings form
* controls in both single site (site and server settings combined) and multisite
* (site and server settings separated).
*
* The coordinator handles UI feedback for all successes and will show and error message
* if any settings fails to save. Specific form controllers should handle form-specific
* error reporting.
*/

export interface IForm {
  name: string;
  state: ISettingsFormScope;
}

export interface ISettingsFormScope  {
  busy?: boolean;
  formController: ng.IFormController;
  cancel: () => void;
  clearInvalid?: (name: string) => void;
  close?: () => void;
  focusByName?: (name:string) => void;
  isBusy?: () => boolean;
  isFormUnchanged?: () => boolean;
  isValid?: () => boolean;
  onSave?: () => ng.IPromise<any>;
  resetForm?: () => void;
  save: () => ng.IPromise<any>;
  showResetBtn?: () => void;
  resetSettings?: () => void;
  updateFailure?: (errors: IError[], site: ISiteSettingsForServerAdmin) => ng.IPromise<any>;
}

export interface ICoordinatedForm {
  state: ISettingsFormScope;
  registerFormStateByName: (state: ISettingsFormScope, name: string) => void;
  clearForms: () => void;
}

export interface ISettingsTabsCtrlScope extends ng.IScope {
  extensionsSettingsTabEnabled: boolean;
  hideNameAndId: boolean;
  showRemoteAgentsSection: boolean;
  server: ServerService;
  showEncryptionStatus: boolean; // This is to control whether or not the extract encryption status shows up. It is only hidden in the create site dialog.
}

export interface ISettingsCoordinatorCtrlScope extends ISettingsTabsCtrlScope, IServerSettingsScope {
  settingsCoordinatorForm: ng.IFormController; // from settingsCoordinator.pug
  sharedForm: ICoordinatedForm;
  localForm: ISettingsFormScope;
  serverSettings?: IServerAdminSettings;
  siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  siteSettings: ISiteSettingsForSiteAdmin;
  siteAuthenticationSettings: ISiteAuthenticationSettings;
  extensionsServerSettings: IExtensionsServerSettings;
  siteId?: string;
  showResetSettingsBtn: () => boolean;
}


export class SettingsFormService {

  public state: ISettingsFormScope;
  private forms: ISettingsFormScope[];

  public static $inject = [
    '$q',
    '$translate',
    '$state',
    '$timeout',
    'Sites',
    'ToasterService'
  ];

  constructor(
    private $q: ng.IQService,
    $translate: ng.translate.ITranslateService,
    $state: ng.ui.IStateService,
    $timeout: ng.ITimeoutService,
    private sitesResource: Sites,
    private toaster: ToasterService
  ) {
    this.forms = [];
    this.state =  {
      formController: null,
      busy: false,
      isBusy: () => _.some(this.forms, (form: ISettingsFormScope) => form.isBusy()),
      isFormUnchanged: () => _.every(this.forms, (form: ISettingsFormScope) => form.isFormUnchanged()),
      isValid: () => _.every(this.forms, (form: ISettingsFormScope) => form.formController ? form.formController.$valid : true),
      save: () => {
        const savePromises =  _.map(this.forms, (form: ISettingsFormScope) => form.save());
        return this.$q.all(savePromises).then((result: any) => {
          if (ResultHelper.hasErrors(result)){
            // TODO: show error ids in toast
            const errorIds = ResultHelper.getErrorIds(result);
            toaster.pop({ type: ToastType.Error, text: $translate.instant('settingsUpdateFailure') });
          }
          else {
            toaster.pop({ type: ToastType.Info, text: $translate.instant('settingsUpdateSuccess') });
          }
        });
      },
      cancel: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.cancel) form.cancel();
        });
        toaster.pop({ type: ToastType.Info, text: $translate.instant('settingsReverted') });
      },
      clearInvalid(inputName: string): void {
        _.each(this.forms, (form: ISettingsFormScope) => {
          const ngModelCtrl: ng.INgModelController = (<any>form.formController)[inputName];
          if (ngModelCtrl.$invalid) {
            ngModelCtrl.$setViewValue(null);
            ngModelCtrl.$render();
            ngModelCtrl.$setPristine();
            _.each(_.keys(ngModelCtrl.$error), (error) => ngModelCtrl.$setValidity(error, true));
          }
        });
      },
      focusByName: (name) => {
        // Timeout allows field to be enabled first
        $timeout(() => angular.element('input[name="'+name+'"], textarea[name="'+name+'"]').focus());
      },
      onSave: () => {
        const onSavePromises = _.map(this.forms, (form: ISettingsFormScope) => {
          if (form.onSave) return form.onSave();
        });
        return this.$q.all(onSavePromises);
      },
      resetForm: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.resetForm) form.resetForm();
        });
      },
      resetSettings: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.resetSettings) form.resetSettings();
        });
      },
      updateFailure: (errors: IError[], site: ISiteSettingsForServerAdmin) => {
        // Handles errors in site settings coordinator and edit site modal and create site modal
        _.each(errors, (error: IError) => {
          this.toaster.pop({ type: ToastType.Error, text: this.sitesResource.errorMessage(error.code, site)});
        });
        return this.$q.when();
      }
    };

  }

  public registerFormStateByName(state: ISettingsFormScope, name: string) {
    this.forms.push(state);
  }

  public clearForms() {
    this.forms = [];
  }

  public showResetSettingsBtn = () => _.some(this.forms, (form: ISettingsFormScope) => !!form.resetSettings);


}

Module.instance().service('SettingsFormService', SettingsFormService);

export class SettingsCoordinatorCtrl {

  public static $inject = [
    '$scope',
    'SettingsFormService',
  ];

  constructor(
    private scope: ISettingsCoordinatorCtrlScope,
    private formService: SettingsFormService,
    siteSettings: ISiteSettingsForSiteAdmin, // from $state resolve to be available for child scopes
    siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
    extensionsServerSettings: IExtensionsServerSettings // only resolved for settings.extensions states
  ) {
    this.formService.state.formController = this.formService.state.formController
      || (scope as any).settingsCoordinatorForm;
    scope.sharedForm = scope.sharedForm || this.formService;
    this.formService.clearForms();
    this.scope.showResetSettingsBtn = this.formService.showResetSettingsBtn;
    this.scope.$on('destroy', () => {
      this.formService.clearForms();
    });
  }


}

Module.instance().directive('tbSettingsCoordinator',
  () => {
    return {
      restrict: 'E',
      replace: true,
      transclude: true,
      scope: true,
      templateUrl: 'settingsCoordinator.html',
      link: ($scope: ISettingsCoordinatorCtrlScope) =>  {
        $scope.sharedForm.state.formController = $scope.settingsCoordinatorForm;
      },
      controller: SettingsCoordinatorCtrl
    };
  }
);
